import { ProjectType, projectTypeName, ProjectTypes } from '../common/constants'
import JobList from '../components/JobList/JobList'
import NewJobModal from '../components/jobs/newJobModal/NewJobModal'
import { useEffect, useState } from 'react'
import { Segmented, Tooltip } from 'antd'
import { faBars, faList, faRectangleList, faTableColumns } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useFeatureFlag from '../hooks/useFeatureFlag'
import { useSavedState } from '../hooks/useSavedState'
import JobKanbanBoard from '../components/JobKanban/JobKanbanBoard'
import {
  CardDensities, CardDensity, cardDensityKey, isCondensed,
  JOB_KANBAN_FEATURE, JobListView, JobListViews, jobListViewKey
} from '../components/JobList/jobListSettings'
import JobListFilters from '../components/JobList/JobListFilters'
import { useJobListFilters } from '../components/JobList/useJobListFilters'
import HeroContainer from '../components/common/HeroContainer'
import HeroButtonsContainer from '../components/common/HeroButtonsContainer'
import HeroButton from '../components/common/HeroButton'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { NavigationService } from '../services/navigationService'
import EstimatesReport from '../components/Datacenter/EstimatesReport/EstimatesReport'

export type Props = {
  jobType?: ProjectType
  deleted?: boolean
  sold?: boolean
  heroImage: string
  navigationService: NavigationService
  googleMapsApiKey: string
}

const JobListPage = ({ jobType, deleted, heroImage, sold = true, navigationService, googleMapsApiKey }: Props) => {
  const [newJobModalVisible, setNewJobModalVisible] = useState(false)
  const name = jobType ? projectTypeName(jobType) + 's' : 'Estimates'

  const kanbanEnabled = useFeatureFlag(JOB_KANBAN_FEATURE)
  const [view, setView] = useSavedState<JobListView>(jobListViewKey({ jobType, deleted }), JobListViews.List)
  const [density, setDensity] = useSavedState<CardDensity>(cardDensityKey({ jobType, deleted }), CardDensities.Condensed)
  const showKanban = kanbanEnabled && view === JobListViews.Kanban
  const filters = useJobListFilters({ jobType, deleted, sold })

  const viewToggle = <Segmented
    value={view}
    onChange={value => setView(value as JobListView)}
    options={[
      { value: JobListViews.List, label: 'List', icon: <FontAwesomeIcon icon={faList} /> },
      { value: JobListViews.Kanban, label: 'Pipeline', icon: <FontAwesomeIcon icon={faTableColumns} /> }
    ]}
  />

  const densityToggle = <Segmented
    value={density}
    onChange={value => setDensity(value as CardDensity)}
    options={[
      {
        value: CardDensities.Detailed,
        icon: <Tooltip title="Full cards"><FontAwesomeIcon icon={faRectangleList} /></Tooltip>
      },
      {
        value: CardDensities.Condensed,
        icon: <Tooltip title="Name and address only"><FontAwesomeIcon icon={faBars} /></Tooltip>
      }
    ]}
  />

  useEffect(() => {
    if (navigationService.hash === 'new') {
      setNewJobModalVisible(true)
    }
  }, [navigationService.hash])

  const onCancelNewJob = () => {
    setNewJobModalVisible(false)
    navigationService.setHash('')
  }

  return <>
    <HeroContainer imgSrc={heroImage} />
    <div className="hero-details">
      {!deleted && <h1>{name}</h1>}
      {deleted && <h1 className="hero-details-deleted-item">
        <span>
          <span className="margin-20">DELETED {name.toUpperCase()}</span>
          <br />
          <small className="display-none-tiny">(click on the restore icon to un-delete a {name.toLowerCase()})</small>
        </span>
      </h1>}
      {!deleted && <HeroButtonsContainer>
        <HeroButton icon={faPlus} onClick={() => setNewJobModalVisible(true)} />
      </HeroButtonsContainer>}
    </div>

    {/* One filter bar, rendered outside the view branch. Switching views cannot move it,
        remount it, or change which filters it offers — only the table below it swaps. */}
    { name !== 'Estimates' && <>
        <JobListFilters
          filters={filters}
          viewToggle={kanbanEnabled ? viewToggle : undefined}
          densityToggle={showKanban ? densityToggle : undefined}
          showSort={showKanban}
        />
        {showKanban
          ? <JobKanbanBoard
            jobType={jobType}
            deleted={deleted}
            sold={sold}
            filters={filters}
            condensed={isCondensed(density)}
          />
          : <section>
            <JobList jobType={jobType} deleted={deleted} sold={sold} filters={filters} />
          </section>}
      </>
    }

    { name === 'Estimates' && !deleted && <section>
        <EstimatesReport
          viewToggle={kanbanEnabled ? viewToggle : undefined}
          densityToggle={densityToggle}
          showKanban={showKanban}
          condensed={isCondensed(density)}
        />
      </section>
    }

    { name === 'Estimates' && deleted && <>
        <JobListFilters filters={filters} />
        <section>
          <JobList jobType={jobType} deleted={deleted} sold={sold} filters={filters} />
        </section>
      </>
    }

    <NewJobModal
      jobType={jobType ?? ProjectTypes.Project}
      isEstimate={!sold}
      visible={newJobModalVisible}
      onCancel={onCancelNewJob}
      googleMapsApiKey={googleMapsApiKey}
    />
  </>
}

export default JobListPage
